<template lang="pug">
  div
    template(
      v-for="(link, index) in breadcrumbs")
      template(v-if="index === 0")
        a(
          class="xl:hidden"
          :href="removeDomainFromUrl(link['url'])"
        )
          Icon(
            :size="16"
            name="i:home")
        a(
          class="hidden xl:inline"
          :href="removeDomainFromUrl(link['url'])"
          v-html="link['text']")
      span(
        v-else-if="breadcrumbs.length >= 4 && index === 1"
        v-text="'...'")
      SelRefined(
        v-else-if="breadcrumbs.length - 2 === index"
        class="parent-links"
        :options="parentLinks"
        :placeholder="link['text']"
        qa-tag="parent_links"
        notInForm)
      span(
        v-if="breadcrumbs.length - 1 !== index"
        class="separator")
      span(
        v-if="breadcrumbs.length - 1 === index"
        :class="breadcrumbs.length >= 4 ? 'short-current-page' : 'current-page'"
        v-html="link['text']")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters } from "vuex"
import { GET_BREADCRUMBS } from "~/store/pages"
import { removeDomainFromUrl } from "~/utils/block"

export default defineComponent({
  props: {
    block: {
      required: true,
      type: Object
    }
  },
  computed: {
    ...mapGetters("pages", [GET_BREADCRUMBS]),

    breadcrumbs() {
      if (this.block && this.block.innerBlocks[0] && this.block.innerBlocks[0].innerHtml) {
        return this[GET_BREADCRUMBS]
      }

      return []
    },

    parentLinks() {
      if (this.block && this.block.innerBlocks[0] && this.block.innerBlocks[0].innerHtml) {
        const items = [...this[GET_BREADCRUMBS]]

        items.splice(0, 1)
        items.splice(items.length - 1, 1)

        const links: string[] = []
        items.forEach((entry, index) => {
          const link: string = '<a href="' + entry.url + '">' + entry.text + "</a>"
          links.push(link)
        })

        return links
      }

      return []
    }
  },

  methods: {
    getSchema() {
      const entries: any = this.breadcrumbs
      const list: Record<string, any> = []
      entries.forEach((entry, index) => {
        const path = this.removeDomainFromUrl(entry.url)
        list.push({
          "@type": "ListItem",
          position: index + 1,
          name: entry.text,
          item:
            this.$config.public.frontendHost +
            (path !== this.$config.public.frontendHost ? path : "")
        })
      })

      if (!entries) {
        return null
      }

      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: list
      }
    },

    removeDomainFromUrl(url) {
      // @ts-ignore: this is not identified by TS
      return removeDomainFromUrl(url, this.$config.public.frontendHost)
    }
  }
})
</script>

<style lang="css" module>
:global {
  .av-breadcrumbs {
    @apply text-base;
    font-weight: 600;

    a > svg {
      @apply align-baseline;
    }

    span.current-page,
    span.short-current-page {
      @apply overflow-ellipsis overflow-hidden whitespace-nowrap inline-block align-bottom text-gray-650;
    }

    span.current-page {
      max-width: calc(70vw - 72px);
    }

    span.short-current-page {
      max-width: calc(70vw - 102px);
    }

    a:hover {
      @apply opacity-50 text-gray-800 no-underline;
    }

    .separator {
      @apply px-ds-sm;

      &:before {
        content: "/";
      }
    }

    .relative {
      @apply inline-block;

      & > div {
        @apply p-0 h-full min-h-full text-base;
        line-height: normal;

        /* Select placeholder */
        & > div:first-child {
          @apply overflow-ellipsis overflow-hidden whitespace-nowrap text-primary;
          max-width: 26vw;

          &:hover {
            @apply opacity-50 text-gray-800;
          }
        }

        /* Select options container */
        & > ul {
          width: max-content;
          max-width: 80vw;

          a {
            @apply overflow-ellipsis overflow-hidden whitespace-nowrap block w-auto px-ds-md py-ds-sm;
            margin: -10px -15px -10px -16px;

            &:hover {
              @apply text-white opacity-100;
            }
          }
        }
      }
    }
  }

  @screen xl {
    .av-breadcrumbs {
      .separator {
        &:before {
          content: ">";
        }
      }

      span.current-page {
        max-width: calc(70vw - 122px);
      }

      span.short-current-page {
        max-width: calc(70vw - 154px);
      }
    }
  }

  @screen rebrandLg {
    .av-breadcrumbs {
      span.current-page {
        max-width: 870px;
      }

      span.short-current-page {
        max-width: 900px;
      }

      .relative {
        & > div {
          /* Select placeholder */
          & > div:first-child {
            max-width: 395px;
          }
        }
      }
    }
  }
}
</style>
